/*eslint complexity: off*/
import React, { useState, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { makeStyles, ThemeProvider } from '@material-ui/core/styles';
import InputLabel from '@material-ui/core/InputLabel';
import Link from '@material-ui/core/Link';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import FaceIcon from '@material-ui/icons/Face';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import BusinessIcon from '@material-ui/icons/Business';
import BackspaceIcon from '@material-ui/icons/Backspace';
import AcUnitIcon from '@material-ui/icons/AcUnit';
import CallMissedOutgoingIcon from '@material-ui/icons/CallMissedOutgoing';
import LinkMaterial from '@material-ui/core/Link';

import {
  corporation,
  createCorporation,
  editCorporation,
  showCorporation,
  showPerson,
  showPack,
  showSpecialty,
} from '~/config/routes';
import { toast } from 'react-toastify';
import { Tooltip } from '@material-ui/core';
import { historyPropType } from '~/config/routerPropTypes';
import Request from '~/providers/Request';
import Paper from '~/components/Paper';
import ModalDelete from '~/components/ModalDelete';
import InputShow from '~/components/InputShow';
import PaperButtons from '~/components/PaperButtons';
import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import DialogUploadFile from '../containers/DialogUploadFile';
import ChipItem from '~/components/Chip';
import makeTheme from '~/assets/styles/makePrimaryColorTheme';
import formatDate from '~/utils/formatDate';
import { packagesColors, packageOptions } from '~/config/constants';
import { AbilityContext } from '~/contexts/Can';
import Can from '~/contexts/Can';
import handleDataAttributes from '../utils/handleDataAttributes';

const useStyles = makeStyles((theme) => ({
  actionDel: {
    color: theme.palette.red.main,
    cursor: 'pointer',
  },
  actionFreeze: {
    color: theme.palette.blue.main,
    cursor: 'pointer',
  },
  actionActive: {
    color: theme.palette.green.main,
    cursor: 'pointer',
  },
}));

function ChipStatus({ record, loading }) {
  const { t } = useTranslation();
  const green = useMemo(() => makeTheme('green'));
  const red = useMemo(() => makeTheme('red'));

  if (loading) {
    return <></>;
  }
  const arr = [];
  if (record.get('disabled_at')) {
    arr.push(
      <ThemeProvider key={1} theme={red}>
        <Chip
          style={{ marginRight: 5 }}
          label={t('corporation.extras.isInactive')}
          color={'primary'}
          component="span"
        />
      </ThemeProvider>
    );
  } else {
    arr.push(
      <ThemeProvider key={1} theme={green}>
        <Chip
          style={{ marginRight: 5 }}
          label={t('corporation.extras.isActive')}
          color={'primary'}
          component="span"
        />
      </ThemeProvider>
    );
  }
  if (record.get('frozen_at')) {
    arr.push(
      <ThemeProvider key={2} theme={red}>
        <Chip
          style={{ marginRight: 5 }}
          label={`${t('corporation.extras.frozenSince')} ${formatDate(
            record.get('frozen_at')
          )}`}
          component="span"
        />
      </ThemeProvider>
    );
  }

  return <>{arr}</>;
}

function ShowCorporation({ id, record, setRecord, loading }) {

  console.log(record);

  const { t } = useTranslation();
  const [loadingAction, setLoadingAction] = useState(false);
  const [openDropDown, setOpenDropDown] = useState(null);
  const [modalInactive, setModalInactive] = useState(false);
  const [modalFreeze, setModalFreeze] = useState(false);
  const [modalFile, setModalFile] = useState(false);
  const classes = useStyles();
  const attrName = (attr) => t(`corporation.attributes.${attr}`);
  const ability = useContext(AbilityContext);

  async function handleDesactive() {
    setLoadingAction(true);
    Request.put(`/admin/corporation/${id}/desactive`)
      .then(async () => {
        const data = await Request.get(`/admin/corporation/${id}`, {});

        toast.success(t('corporation.extras.desactiveSuccess'));
        setRecord(record.updateAttributes(handleDataAttributes(data.data)));
        setLoadingAction(false);
      })
      .catch(() => {
        setLoadingAction(false);
        toast.error(t('corporation.extras.desactiveError'));
      });
    setModalInactive(false);
    setOpenDropDown(null);
  }

  async function handleFreeze() {
    setLoadingAction(true);
    Request.put(`/admin/corporation/${id}/freeze`)
      .then(async () => {
        const data = await Request.get(`/admin/corporation/${id}`, {});

        toast.success(t('corporation.extras.frozenSuccess'));
        setRecord(record.updateAttributes(handleDataAttributes(data.data)));
        setLoadingAction(false);
      })
      .catch(() => {
        setLoadingAction(false);
        toast.error(t('corporation.extras.frozenError'));
      });
    setModalFreeze(false);
    setOpenDropDown(null);
  }

  async function onActive() {
    setLoadingAction(true);
    Request.put(`/admin/corporation/${id}/active`)
      .then(async () => {
        const data = await Request.get(`/admin/corporation/${id}`, {});

        setRecord(record.updateAttributes(handleDataAttributes(data.data)));
        setLoadingAction(false);
        toast.success(t('corporation.extras.activeSuccess'));
      })
      .catch(() => {
        setLoadingAction(false);
        toast.error(t('corporation.extras.activeError'));
      });
    setOpenDropDown(null);
  }

  async function onUnfreeze() {
    setLoadingAction(true);
    Request.put(`/admin/corporation/${id}/unFreeze`)
      .then(async () => {
        const data = await Request.get(`/admin/corporation/${id}`, {});

        setRecord(record.updateAttributes(handleDataAttributes(data.data)));
        setLoadingAction(false);
        toast.success(t('corporation.extras.unFreezeSuccess'));
      })
      .catch(() => {
        setLoadingAction(false);
        toast.error(t('corporation.extras.unFreezeError'));
      });
    setOpenDropDown(null);
  }

  return (
    <>
      <Paper
        Icon={BusinessIcon}
        iconColor="blue"
        title={
          <>
            {t('corporation.defSingular')}{' '}
            <ChipStatus record={record} loading={loading} />
          </>
        }
        loading={loading}
        actions={
          <PaperButtons
            id={id}
            openDropDown={openDropDown}
            setOpenDropDown={setOpenDropDown}
            backRoute={corporation}
            setImportFileModal={
              ability.can('import', 'Corporations') ? setModalFile : null
            }
            newRoute={
              ability.can('new', 'Corporations') ? createCorporation : null
            }
            editRoute={
              ability.can('new', 'Corporations') ? editCorporation : null
            }
            DropDownActions={() => {
              const arr = [];
              if (record.get('disabled_at')) {
                {
                  arr.push(
                    <Can key={1} I="delete" a="Corporations">
                      <MenuItem
                        onClick={onActive}
                        className={classes.actionActive}
                      >
                        <CallMissedOutgoingIcon style={{ marginRight: 5 }} />{' '}
                        {t('corporation.extras.active')}
                      </MenuItem>
                    </Can>
                  );
                }
              } else {
                {
                  arr.push(
                    <Can key={2} I="delete" a="Corporations">
                      <MenuItem
                        onClick={() => setModalInactive(true)}
                        className={classes.actionDel}
                      >
                        <BackspaceIcon style={{ marginRight: 5 }} />{' '}
                        {t('corporation.extras.desactive')}
                      </MenuItem>
                    </Can>
                  );
                }
              }
              if (record.get('frozen_at')) {
                {
                  arr.push(
                    <Can key={3} I="delete" a="Corporations">
                      <MenuItem
                        onClick={onUnfreeze}
                        className={classes.actionFreeze}
                      >
                        <AcUnitIcon style={{ marginRight: 5 }} />{' '}
                        {t('corporation.extras.unfreeze')}
                      </MenuItem>
                    </Can>
                  );
                }
              } else {
                {
                  arr.push(
                    <Can key={4} I="delete" a="Corporations">
                      <MenuItem
                        onClick={() => setModalFreeze(true)}
                        className={classes.actionFreeze}
                      >
                        <AcUnitIcon style={{ marginRight: 5 }} />{' '}
                        {t('corporation.extras.freeze')}
                      </MenuItem>
                    </Can>
                  );
                }
              }
              return <>{arr}</>;
            }}
          />
        }
      >
        <GridContainer>
          <GridItem xs={12} sm={2} md={2} lg={2} xl={2}>
            <InputShow
              value={record.get('country').name}
              label={attrName('country')}
            />
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={2} md={2} lg={2} xl={2}>
            <InputShow value={record.get('id')} label={attrName('id')} />
          </GridItem>
          <GridItem xs={12} sm={5} md={5} lg={5} xl={5}>
            <InputShow value={record.get('name')} label={attrName('name')} />
          </GridItem>
          <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
            <InputShow value={record.get('cnpj')} label={attrName('cnpj')} />
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
            <InputShow
              value={record.get('phone')}
              label={attrName('phone')}
              extra={record.get('country')}
            />
          </GridItem>
          <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
            <InputShow value={record.get('email')} label={attrName('email')} />
          </GridItem>
          <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
            <InputShow
              value={moment(record.get('contract_expires_at')).format(
                'DD/MM/YYYY'
              )}
              label={attrName('contract_expires_at')}
            />
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
            <InputShow
              value={record.get('postal_code')}
              label={attrName('postal_code')}
            />
          </GridItem>
          <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
            <InputShow
              value={record.get('city')}
              digObject={['name']}
              type={'object'}
              label={attrName('city')}
            />
          </GridItem>
          <GridItem xs={12} sm={2} md={2} lg={2} xl={2}>
            <InputShow
              value={record.get('district')}
              label={attrName('district')}
            />
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
            <InputShow
              value={record.get('street')}
              label={attrName('street')}
            />
          </GridItem>
          <GridItem xs={12} sm={1} md={1} lg={1} xl={1}>
            <InputShow
              value={record.get('number')}
              label={attrName('number')}
            />
          </GridItem>
          <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
            <InputShow
              value={record.get('contact_name')}
              label={attrName('contact_name')}
            />
          </GridItem>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputShow
              value={record.get('contact_phone')}
              label={attrName('contact_phone')}
            />
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
            <InputShow
              value={record.get('users_limit')}
              label={attrName('users_limit')}
            />
          </GridItem>
        </GridContainer>

        {/* Novos campos de Franquia/Seguradora */}
        <GridContainer>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputShow
              value={
                record.get('config_corporation')?.corporation_type === 'Cliente'
                  ? 'Cliente'
                  : record.get('config_corporation')?.corporation_type === 'franchise'
                  ? 'Franquia'
                  : record.get('config_corporation')?.corporation_type === 'insurance'
                  ? 'Seguradora'
                  : 'Cliente'
              }
              label="Tipo de Empresa"
            />
          </GridItem>
          {(record.get('config_corporation') &&
            (record.get('config_corporation')?.corporation_type === 'franchise' ||
             record.get('config_corporation')?.corporation_type === 'insurance')) ? (
            <>
                            <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
                <InputShow
                  value={record.get('config_corporation')?.income_value ?
                    `R$ ${record.get('config_corporation').income_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` :
                    '-'
                  }
                  label="Renda média mensal"
                />
              </GridItem>
              <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
                <InputShow
                  value={record.get('config_corporation')?.accession_percentage ?
                    `${record.get('config_corporation').accession_percentage}%` :
                    '-'
                  }
                  label="% Split - Taxa de adesão"
                />
              </GridItem>
              <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
                <InputShow
                  value={record.get('config_corporation')?.signature_percentage ?
                    `${record.get('config_corporation').signature_percentage}%` :
                    '-'
                  }
                  label="% Split - Plano mensal"
                />
              </GridItem>
            </>
          ) : null}
        </GridContainer>

        {(record.get('config_corporation') &&
          (record.get('config_corporation')?.corporation_type === 'franchise' ||
           record.get('config_corporation')?.corporation_type === 'insurance')) ? (
          <GridContainer>
            <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
              <InputShow
                value={record.get('config_corporation')?.wallet_id || '-'}
                label="ID da conta"
              />
            </GridItem>
            <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
              <InputShow
                value={record.get('config_corporation')?.landing_url || '-'}
                label="ID da Landing Page"
              />
            </GridItem>
          </GridContainer>
        ) : null}
        <GridContainer justifyContent="space-between">
          <GridItem xs={12} sm={2} md={2} lg={2} xl={2}>
            <InputShow
              value={
                record.get('config_corporation') === null
                  ? ''
                  : record.get('config_corporation')?.required_cns
                  ? 'SIM'
                  : 'NÃO'
              }
              label={attrName('required_cns')}
            />
          </GridItem>
          <GridItem xs={12} sm={2} md={2} lg={2} xl={2}>
            <Tooltip title="Essa obrigação será aplicada para restringir a consulta dos usuários associados a essa empresa, limitando o atendimento ao município onde a empresa está localizada.">
              <div>
                <InputShow
                  value={
                    record.get('config_corporation') === null
                      ? ''
                      : record.get('config_corporation')?.required_location
                      ? 'SIM'
                      : 'NÃO'
                  }
                  label={attrName('required_location')}
                />
              </div>
            </Tooltip>
          </GridItem>
          <GridItem xs={12} sm={2} md={2} lg={2} xl={2}>
            <div>
              <InputShow
                value={
                  record.get('config_corporation') === null
                    ? ''
                    : record.get('config_corporation')?.required_documents
                    ? 'SIM'
                    : 'NÃO'
                }
                label={attrName('required_documents')}
              />
            </div>
          </GridItem>
          <GridItem xs={12} sm={2} md={2} lg={2} xl={2}>
            <InputShow
              value={
                record.get('config_corporation') === null
                  ? ''
                  : record.get('config_corporation')?.has_benefits_club
                  ? 'SIM'
                  : 'NÃO'
              }
              label={attrName('has_benefits_club')}
            />
          </GridItem>
          <GridItem xs={12} sm={2} md={2} lg={2} xl={2}>
            <InputShow
              value={
                record.get('config_corporation') === null
                  ? ''
                  : record.get('config_corporation')?.required_email
                  ? 'SIM'
                  : 'NÃO'
              }
              label={attrName('required_email')}
            />
          </GridItem>
          {record.get('country').id === '2' && (
            <GridItem xs={12} sm={2} md={2} lg={2} xl={2}>
              <InputShow
                value={
                  record.get('config_corporation') === null
                    ? ''
                    : record.get('config_corporation')?.user_welcome_email
                    ? 'SIM'
                    : 'NÃO'
                }
                label={attrName('user_welcome_email')}
              />
            </GridItem>
          )}
        </GridContainer>

        <GridContainer>
          <GridItem>
            <InputLabel shrink style={{ marginBottom: 10 }}>
              {t('corporation.attributes.corporationFather')}
            </InputLabel>
            {record.get('corporation') && (
              <Link
                target="_blank"
                rel="noopener"
                href={showCorporation.reverse({
                  id: record.get('corporation').id,
                })}
              >
                <Chip
                  icon={<BusinessIcon />}
                  label={record.get('corporation').name}
                  variant="outlined"
                  color="primary"
                  onDelete={() => {}}
                  deleteIcon={<OpenInNewIcon />}
                />
              </Link>
            )}
            {!record.get('corporation') && (
              <Chip
                label={attrName('corporationNoFather')}
                variant="outlined"
                color="secondary"
              />
            )}
          </GridItem>
          <GridItem xs={12}>
            <InputLabel shrink style={{ marginBottom: 10 }}>
              {attrName('owners')}
            </InputLabel>
            {record.get('owners') &&
              record.get('owners').map((item) => (
                <Link
                  target="_blank"
                  rel="noopener"
                  key={item.id}
                  href={showPerson.reverse({ id: item.id })}
                >
                  <Chip
                    style={{ marginRight: 5 }}
                    icon={<FaceIcon />}
                    key={item.id}
                    label={item.name}
                    variant="outlined"
                    color="primary"
                    onDelete={() => {}}
                    deleteIcon={<OpenInNewIcon />}
                  />
                </Link>
              ))}
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={6}>
            <InputLabel shrink style={{ marginBottom: 10 }}>
              {attrName('packPackages')}
            </InputLabel>
            {record.get('packs') &&
              record.get('packs').map((item) => (
                <Chip
                  label={item.name}
                  key={item.id}
                  component={LinkMaterial}
                  href={showPack.reverse({ id: item.id })}
                  style={{
                    cursor: 'pointer',
                    marginRight: 5,
                    backgroundColor: packagesColors['pack'],
                  }}
                  target="_blank"
                  color="primary"
                  onDelete={() => {}}
                  deleteIcon={<OpenInNewIcon />}
                />
              ))}
            {record.get('packs').length <= 0 && (
              <Chip
                label={attrName('noPacks')}
                variant="outlined"
                color="secondary"
              />
            )}
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12}>
            <InputLabel shrink style={{ marginBottom: 5 }}>
              {t('corporation.attributes.activePacks')}
            </InputLabel>
            {record.get('is_doctor') && (
              <ChipItem
                label={packageOptions['doctor']}
                colorHex={packagesColors['doctor']}
              />
            )}
            {record.get('is_doctor_all') && (
              <ChipItem
                label={packageOptions['doctor_all']}
                colorHex={packagesColors['doctor_all']}
              />
            )}
            {record.get('is_emergency') && (
              <ChipItem
                label={packageOptions['emergency']}
                colorHex={packagesColors['emergency']}
              />
            )}
            {record.get('is_driving') && (
              <ChipItem
                label={packageOptions['driving']}
                colorHex={packagesColors['driving']}
              />
            )}
            {record.get('is_plan') && (
              <ChipItem
                label={packageOptions['users']}
                colorHex={packagesColors['users']}
              />
            )}
          </GridItem>
        </GridContainer>

        <GridContainer>
          <GridItem xs={6}>
            <InputLabel shrink style={{ marginBottom: 10 }}>
              {attrName('specialties')}
            </InputLabel>
            {record.get('specialties') &&
              record
                .get('specialties')
                .map((item) => (
                  <Chip
                    label={item.name}
                    key={item.id}
                    component={LinkMaterial}
                    href={showSpecialty.reverse({ id: item.specialty_id })}
                    style={{ cursor: 'pointer', marginRight: 5 }}
                    target="_blank"
                    color="primary"
                    onDelete={() => {}}
                    deleteIcon={<OpenInNewIcon />}
                  />
                ))}
            {record.get('specialties').length <= 0 && (
              <Chip
                label={attrName('noSpecialties')}
                variant="outlined"
                color="secondary"
              />
            )}
          </GridItem>
        </GridContainer>

        {!!record.get('branches') && !!record.get('branches').length && (
          <GridContainer>
            <GridItem xs={6}>
              <InputLabel shrink style={{ marginBottom: 10 }}>
                {t('corporation.attributes.linked_corporations')}
              </InputLabel>
              {record.get('branches').map((item) => (
                <Link
                  target="_blank"
                  rel="noopener"
                  key={item.id}
                  href={showCorporation.reverse({ id: item.id })}
                >
                  <Chip
                    style={{ marginRight: 5 }}
                    icon={<BusinessIcon />}
                    key={item.id}
                    label={item.name}
                    variant="outlined"
                    color="primary"
                    onDelete={() => {}}
                    deleteIcon={<OpenInNewIcon />}
                  />
                </Link>
              ))}
            </GridItem>
          </GridContainer>
        )}
      </Paper>

      <ModalDelete
        modal={modalInactive}
        isLoading={loadingAction}
        onClose={() => {
          setModalInactive(false);
          setOpenDropDown(null);
        }}
        message={t('corporation.extras.desactiveConfirm')}
        textDelete={t('corporation.extras.desactive')}
        deleteAction={handleDesactive}
      />
      <ModalDelete
        modal={modalFreeze}
        isLoading={loadingAction}
        iconColor="primary"
        buttonColor="blue"
        icon={AcUnitIcon}
        onClose={() => {
          setModalFreeze(false);
          setOpenDropDown(null);
        }}
        message={t('corporation.extras.freezeConfirm')}
        textDelete={t('corporation.extras.freeze')}
        deleteAction={handleFreeze}
      />
      {modalFile && (
        <DialogUploadFile
          corpotarion={record}
          handleClose={() => setModalFile(false)}
        />
      )}
    </>
  );
}

ShowCorporation.propTypes = {
  history: historyPropType,
};

export default ShowCorporation;
